import { QuestionnaireInterface } from '@api/assessmentQuestionnaireApiHandler';
import { DEFAULT_V2_IMAGE_PATH } from '@config/questionnaire-v2-images.config';
import { KeyConfigurations } from '@enums/KeyConfigurations';
import { colorMap } from '@helpers/color.helper';
import { LineProgressBar, MotionImage, MotionText } from '@shared/atoms';
import { AnswerOptions, QuestionNavigation, ShareThoughtDrawer } from '@shared/molecules';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { ICON_BG_FALL_BACK_COLOR } from '../SingleQuestionnaire/config';
import styles from './index.module.scss';

interface QuestionCardProps {
  question: {
    id: number;
    questionnaireId: number;
    categoryId: number;
    responseScaleId: number;
    questionText: string;
    orderId: number;
    required: boolean;
    icon: string | null;
    buttonText: string | null;
    description: string | null;
    isBrakeOff: boolean;
    answers: { id: number; optionText: string; score: number }[];
  };
  progress: number;
  isLastQuestion: boolean;
  isFirstQuestion: boolean;
  onAnswer: (answerId: number, answerScore: number) => void;
  onPrevious: () => void;
  className?: string;
  shareThoughtEnabled?: boolean;
  currentQuestionNumber?: number;
  totalQuestions?: number;
  questionnaire: QuestionnaireInterface | null;
  totalQuestionsWithoutBrakeOff: number | null;
  currentQuestionIndexWithoutBrakeOff: number | null;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  progress,
  isFirstQuestion,
  isLastQuestion,
  onAnswer,
  onPrevious,
  className,
  shareThoughtEnabled,
  currentQuestionNumber,
  totalQuestions,
  questionnaire,
  totalQuestionsWithoutBrakeOff,
  currentQuestionIndexWithoutBrakeOff,
}) => {
  const [open, setOpen] = useState(false);
  const questionId = `question-${question.id}`;
  const headingId = `question-heading-${question.id}`;
  const questionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (questionRef.current) {
      questionRef.current.focus();
    }
  }, [question.id]);

  const formattedProgress = Math.round(progress);
  const progressDescription = `Assessment progress: ${formattedProgress}%`;

  return (
    <div
      className={styles.container}
      role="form"
      aria-labelledby={headingId}
      tabIndex={-1}
      ref={questionRef}
    >
      <div className={className}>
        <div aria-live="polite" aria-atomic="true" className="visually-hidden">
          {currentQuestionNumber &&
            totalQuestions &&
            `Question ${currentQuestionNumber} of ${totalQuestions}`}
        </div>

        <div className={styles.top}>
          {/* <MotionText as="h3" text={`${(questionnaire && questionnaire?.name) || ''} Assessment`} />

          <MotionText as="p" text={(questionnaire && questionnaire?.shortDescription) || ''} /> */}

          {/* <div className={styles.progressInfo}>
            <div className={styles.questionProgress}>
              Question {currentQuestionIndexWithoutBrakeOff || 1} of{' '}
              {totalQuestionsWithoutBrakeOff || 0}
            </div>
            <div className={styles.completionPercentage}>
              {Math.round(progress || 0)}% completed
            </div>
          </div> */}
        </div>

        <LineProgressBar
          progress={progress}
          aria-label={progressDescription}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={formattedProgress}
        />

        {question?.icon && (
          <div
            className={styles.iconCircleDesktop}
            aria-hidden="true"
            style={{
              background:
                questionnaire && questionnaire?.color
                  ? colorMap[questionnaire?.color]?.lightColor
                  : ICON_BG_FALL_BACK_COLOR,
            }}
          >
            <MotionImage
              src={`${DEFAULT_V2_IMAGE_PATH}/${question.icon}`}
              alt="Question Image"
              className={styles.topIconImage}
              animation={{
                initial: { opacity: 0, scale: 0.8 },
                animate: { opacity: 1, scale: 1 },
                exit: { opacity: 0, scale: 0.8 },
                transition: { duration: 0.5 },
              }}
              skeletonWidth="40px"
              skeletonHeight="40px"
              shape="circle"
            />
          </div>
        )}

        {question?.icon && (
          <div
            className={styles.iconCircleMobile}
            aria-hidden="true"
            style={{
              background:
                questionnaire && questionnaire?.color
                  ? colorMap[questionnaire?.color]?.lightColor
                  : ICON_BG_FALL_BACK_COLOR,
            }}
          >
            <MotionImage
              src={`${DEFAULT_V2_IMAGE_PATH}/${question.icon}`}
              alt="Question Image"
              className={styles.topIconImage}
              animation={{
                initial: { opacity: 0, scale: 0.8 },
                animate: { opacity: 1, scale: 1 },
                exit: { opacity: 0, scale: 0.8 },
                transition: { duration: 0.5 },
              }}
              skeletonWidth="30px"
              skeletonHeight="30px"
              shape="circle"
            />
          </div>
        )}

        {/* <MotionText as="h3" text="Over the last 2 weeks" className={styles.subTitle} /> */}
        <AnimatePresence mode="wait">
          <motion.div
            key={question.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: { delay: 0.1, duration: 0.3, ease: 'easeOut' },
            }}
            exit={{ opacity: 0, y: -10, transition: { duration: 0.2 } }}
            className={styles.questionContainer}
          >
            <MotionText
              as="h4"
              text={question.questionText}
              className={styles.question}
              id={headingId}
            />
            {shareThoughtEnabled && (
              <MotionText
                as="button"
                text="Share thoughts"
                className={styles.shareThoughtText}
                onClick={() => setOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === KeyConfigurations.ENTER || e.key === KeyConfigurations.SPACE) {
                    e.preventDefault();
                    setOpen(true);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-haspopup="dialog"
                aria-expanded={open}
              />
            )}

            <AnswerOptions
              answers={question.answers}
              onSelectAnswer={onAnswer}
              qId={question.id}
              aria-labelledby={headingId}
            />
            <QuestionNavigation
              onPrevious={onPrevious}
              onNext={() => {
                if (!isLastQuestion) onAnswer(0, 0);
              }}
              showPrevious={!isFirstQuestion}
              currentQuestionNumber={currentQuestionNumber}
              totalQuestions={totalQuestions}
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {shareThoughtEnabled && (
        <ShareThoughtDrawer
          open={open}
          handleClose={() => setOpen(false)}
          handleOpen={() => setOpen(true)}
          questionId={question.id}
          questionLabel={question?.questionText || ''}
        />
      )}
    </div>
  );
};

QuestionCard.defaultProps = {
  className: styles.card,
  shareThoughtEnabled: false,
  currentQuestionNumber: undefined,
  totalQuestions: undefined,
};
